import { chunk } from 'lodash'

import { AppThunk } from '@/store'
import { createAction, request, requestLimit } from '@/store/actions'

import {
  ADD_MEMBER_TO_DRAFT_NOTIFICATION_AUDIENCE,
  FETCH_TARGETING_AGGREGATE_SUGGESTIONS,
  FETCH_TARGETING_RULE_SET_SUGGESTIONS,
  REMOVE_MEMBER_FROM_DRAFT_NOTIFICATION_AUDIENCE,
  RESET_BULK_TARGETING_STATUS,
  RESET_DRAFT_NOTIFICATION,
  RESOLVE_BULK_TARGETING_AGGREGATES,
  RESOLVE_BULK_TARGETING_RULE_SETS,
  START_DRAFT_NOTIFICATION
} from './actionTypes'

const CHUNK_SIZE = 1000

export const startNotification = (
  activeStep: unknown,
  notification: unknown,
  settings?: unknown,
  audience?: unknown
): AppThunk => {
  return dispatch => dispatch(createAction(START_DRAFT_NOTIFICATION, { activeStep, audience, notification, settings }))
}

export const addAudienceMember = (rule: unknown, urn: unknown): AppThunk => {
  return dispatch => dispatch(createAction(ADD_MEMBER_TO_DRAFT_NOTIFICATION_AUDIENCE, { rule, urn }))
}

export const removeAudienceMember = (rowIndex: number): AppThunk => {
  return dispatch => dispatch(createAction(REMOVE_MEMBER_FROM_DRAFT_NOTIFICATION_AUDIENCE, { rowIndex }))
}

export const resetNotification = (): AppThunk => {
  return dispatch => dispatch(createAction(RESET_DRAFT_NOTIFICATION))
}

export const fetchTargetingAggregateSuggestions = ({
  count,
  filter,
  groupBy
}: {
  count: unknown
  filter: unknown
  groupBy: unknown
}): AppThunk => {
  return dispatch => {
    return dispatch(
      request(
        '/organizations/:organizationId/users/aggregate',
        FETCH_TARGETING_AGGREGATE_SUGGESTIONS,
        { method: 'POST', body: { count, filter, groupBy, spaceId: ':spaceId' } },
        { args: { urn: groupBy }, ignoreLeading: true }
      )
    )
  }
}

export const fetchTargetingRuleSetSuggestions = (
  ruleSet: unknown,
  {
    attributes,
    count,
    filter,
    guid,
    email,
    activeOnly
  }: { attributes: unknown; count: unknown; filter: unknown; guid: unknown; email: unknown; activeOnly: unknown }
): AppThunk => {
  return dispatch => {
    return dispatch(
      request(
        '/organizations/:organizationId/users/search',
        FETCH_TARGETING_RULE_SET_SUGGESTIONS,
        { method: 'POST', body: { attributes, count, filter, guid, email, activeOnly, spaceId: ':spaceId' } },
        { args: { ruleSet }, ignoreLeading: true }
      )
    )
  }
}

export const resetBulkTargetingStatus = (): AppThunk => {
  return dispatch => dispatch(createAction(RESET_BULK_TARGETING_STATUS))
}

export const resolveBulkTargetingAggregates = (
  aggregates: Record<any, { filters: unknown[]; groupBy: unknown; urn: unknown }>,
  addToAudience = true
): AppThunk => {
  return dispatch => {
    const requests = Object.values(aggregates).reduce(
      (requests, { filters, groupBy, urn }) =>
        requests.concat(
          // @ts-expect-error
          chunk(filters, CHUNK_SIZE).map(batch => [
            '/organizations/:organizationId/users/aggregate',
            RESOLVE_BULK_TARGETING_AGGREGATES,
            { method: 'POST', body: { filter: batch, groupBy: groupBy || urn, spaceId: ':spaceId' } },
            { args: { addToAudience, processed: batch.length, urn } }
          ])
        ),
      []
    )

    return dispatch(requestLimit(requests, 1))
  }
}

/**
 * Resolve the bulk targeting rulesets into network calls
 *
 * @param ruleSets - `email` and `guid` are two valid types of rulesets
 * @param ruleSets.filters - The filters to apply to the type of ruleset
 * @param addToAudience
 * @returns
 */
export const resolveBulkTargetingRuleSets = (
  ruleSets: Record<any, { urn?: unknown; filters: unknown[] }>,
  addToAudience = true
): AppThunk => {
  const valueRuleSets = new Set(['email', 'guid'])
  return dispatch => {
    const requests = Object.keys(ruleSets).reduce((requests, ruleSet) => {
      const ruleSetType = valueRuleSets.has(ruleSet.toLowerCase()) ? ruleSet : 'filter'
      return requests.concat(
        // @ts-expect-error
        chunk(ruleSets[ruleSet].filters, CHUNK_SIZE).map(batch => {
          return [
            '/organizations/:organizationId/users/searchbasic',
            RESOLVE_BULK_TARGETING_RULE_SETS,
            { method: 'POST', body: { [ruleSetType]: batch, activeOnly: true, spaceId: ':spaceId' } },
            {
              shouldNotClearError: true,
              args: { addToAudience, processed: batch.length, ruleSet, urn: ruleSets[ruleSet].urn }
            }
          ]
        })
      )
    }, [])

    return dispatch(requestLimit(requests, 1))
  }
}
